import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { clientsQuery } from '../api/clients.ts';
import { meQuery } from '../api/me.ts';
import {
  createQuote,
  dayLabel,
  eventDays,
  eventSoldWindow,
  type EventWindowsInput,
  quotesQuery,
  type Quote,
  soldWindowFor,
  STATUS_LABELS,
  TYPE_LABELS,
  type QuoteSummary,
} from '../api/quotes.ts';
import { createVenue, venuesQuery } from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { formatMinorUnits } from '../money.ts';
import { type UnaddedVenue, VenuePicker } from './venue-field.tsx';

// CB-REQ QUO-01, the priced quote (v1.943). The list shows the latest version of each quote; a
// quote starts with its client and its dates, and its lines are added on the quote's own page.

export function QuotesScreen() {
  const quotes = useQuery(quotesQuery);
  const me = useQuery(meQuery);
  // VEN-01 at v1.9104: a venue's Quote here opens the new quote at that venue.
  const { venue: atVenue } = useSearch({ strict: false }) as { venue?: string };
  const [creating, setCreating] = useState(atVenue !== undefined);
  const mayManage = me.data?.capabilities.includes('quotes.manage') ?? false;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Quotes"
        title="Quotes"
        meta={
          quotes.data
            ? `${quotes.data.length} ${quotes.data.length === 1 ? 'quote' : 'quotes'}`
            : undefined
        }
        actions={
          mayManage && !creating ? (
            <Button onClick={() => setCreating(true)}>New quote</Button>
          ) : null
        }
      />
      {creating ? <NewQuoteForm venueId={atVenue} onCancel={() => setCreating(false)} /> : null}
      {quotes.isPending ? <p className="text-muted">Loading…</p> : null}
      {quotes.isError ? (
        <p role="alert" className="alert">
          {quotes.error.message}
        </p>
      ) : null}
      {quotes.data?.length === 0 ? <p className="text-ink-soft">No quotes yet.</p> : null}
      {quotes.data && quotes.data.length > 0 ? <QuoteTable rows={quotes.data} /> : null}
    </div>
  );
}

function StatusTag({ status, expired }: { status: QuoteSummary['status']; expired: boolean }) {
  // Expired is advisory (QUO-01): the quote can still be accepted once its prices are refreshed.
  if (expired) return <span className="tag tag-accent-2">Expired</span>;
  return (
    <span className={status === 'draft' ? 'tag' : status === 'sent' ? 'tag tag-accent' : 'tag'}>
      {STATUS_LABELS[status]}
    </span>
  );
}

function QuoteTable({ rows }: { rows: QuoteSummary[] }) {
  const seesTotals = rows.some((r) => r.total !== undefined);
  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <caption className="sr-only">Quotes, newest first</caption>
        <thead>
          <tr>
            <th scope="col">Quote</th>
            <th scope="col">Client</th>
            <th scope="col">Charged</th>
            <th scope="col">Status</th>
            {seesTotals ? (
              <th scope="col" className="text-right">
                Total inc GST
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((q) => (
            <tr key={q.id}>
              <th scope="row" className="font-semibold">
                <Link to="/quotes/$quoteId" params={{ quoteId: q.id }} className="link">
                  {q.reference}
                </Link>
                {q.version > 1 ? <span className="text-muted"> v{q.version}</span> : null}
              </th>
              <td>
                {q.client.name}
                {q.type === 'produced_event' ? (
                  <span className="text-xs text-muted"> · {TYPE_LABELS[q.type]}</span>
                ) : null}
              </td>
              <td className="text-sm text-ink-soft">
                {dayLabel(q.soldWindow.days[0]!)}
                {q.soldWindow.days.length > 1 ? ` – ${dayLabel(q.soldWindow.days.at(-1)!)}` : ''}
              </td>
              <td>
                <StatusTag status={q.status} expired={q.expired} />
              </td>
              {seesTotals ? (
                <td className="text-right tabular-nums">
                  {q.total ? formatMinorUnits(q.total.amountMinor, q.total.currency) : '—'}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewQuoteForm({
  venueId: startAt,
  onCancel,
}: {
  /** The venue it starts at, from a venue's Quote here. */
  venueId?: string | undefined;
  onCancel: () => void;
}) {
  const clients = useQuery(clientsQuery);
  const venues = useQuery(venuesQuery);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [clientId, setClientId] = useState('');
  // CB-REQ JOB-02 at v1.958: chosen once, for the whole quote chain.
  const [type, setType] = useState<Quote['type']>('dry_hire');
  const [venueId, setVenueId] = useState<string | null>(startAt ?? null);
  // VEN-01 at v1.971: a venue named in the picker but not added yet is added when the quote is.
  const [unaddedVenue, setUnaddedVenue] = useState<UnaddedVenue | null>(null);
  const venue = venues.data?.find((v) => v.id === venueId) ?? null;
  // VEN-01 at v1.971: someone who looks after exactly one venue starts there.
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (prefilled || !venues.data) return;
    setPrefilled(true);
    if (startAt) return;
    const mine = venues.data.filter((v) => v.mine && v.listed);
    if (mine.length === 1) setVenueId(mine[0]!.id);
  }, [prefilled, venues.data]);
  const [out, setOut] = useState('');
  const [back, setBack] = useState('');
  const [events, setEvents] = useState<EventWindowsInput>({
    bumpIn: { start: '', end: '' },
    event: { start: '', end: '' },
    bumpOut: { start: '', end: '' },
  });
  const [chargedFrom, setChargedFrom] = useState('');
  const [chargedTo, setChargedTo] = useState('');
  const [site, setSite] = useState({ contactName: '', contactPhone: '', loadNotes: '' });
  const [notes, setNotes] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async (input: Parameters<typeof createQuote>[0]) => {
      if (!unaddedVenue) return createQuote(input);
      const added = await createVenue(unaddedVenue);
      await queryClient.invalidateQueries({ queryKey: venuesQuery.queryKey });
      return createQuote({ ...input, venueId: added.id });
    },
    onSuccess: async (quote) => {
      await queryClient.invalidateQueries({ queryKey: quotesQuery.queryKey });
      await navigate({ to: '/quotes/$quoteId', params: { quoteId: quote.id } });
    },
  });

  const setEvent = (which: keyof EventWindowsInput, end: 'start' | 'end', value: string) =>
    setEvents((e) => ({ ...e, [which]: { ...e[which], [end]: value } }));

  function submit(event: FormEvent) {
    event.preventDefault();
    setLocalError(null);
    if (!clientId) return setLocalError('Choose the client.');
    const extras = {
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(venueId ? { venueId } : {}),
    };
    if (type === 'produced_event') {
      const times = [events.bumpIn, events.event, events.bumpOut];
      if (times.some((w) => !w.start || !w.end)) {
        return setLocalError('Give the bump-in, the event and the bump-out, each from and to.');
      }
      if (times.some((w) => w.end <= w.start)) {
        return setLocalError(
          'Each of the bump-in, the event and the bump-out ends after it starts.',
        );
      }
      if (events.event.start < events.bumpIn.end || events.bumpOut.start < events.event.end) {
        return setLocalError('The bump-in comes before the event, and the bump-out after it.');
      }
      // OPS-04 at v1.958: charged for the event's days unless said otherwise.
      const days = eventDays(events);
      const soldWindow = eventSoldWindow(events, chargedFrom || days.first, chargedTo || days.last);
      return create.mutate({
        clientId,
        type,
        eventWindows: events,
        soldWindow,
        site: {
          contactName: site.contactName.trim() || null,
          contactPhone: site.contactPhone.trim() || null,
          loadNotes: site.loadNotes.trim() || null,
        },
        ...extras,
      });
    }
    if (!out || !back) return setLocalError('Say when the gear goes out and comes back.');
    if (back <= out) return setLocalError('The gear has to come back after it goes out.');
    // CB-CONV §12: every day of the hire is charged by default; the quote page narrows it per line.
    const from = chargedFrom || out.slice(0, 10);
    const to = chargedTo || back.slice(0, 10);
    if (to < from) return setLocalError('The last charged day cannot be before the first.');
    const scheduledWindow = { start: out, end: back };
    create.mutate({
      clientId,
      type,
      scheduledWindow,
      soldWindow: soldWindowFor(scheduledWindow, from, to),
      ...extras,
    });
  }

  const error = localError ?? (create.isError ? create.error.message : null);

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4">
      <h2 className="section-title">New quote</h2>
      {error ? (
        <p role="alert" className="alert">
          {error}
        </p>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="quote-client" className="text-xs text-ink-soft">
          Client
        </label>
        <select
          id="quote-client"
          className="input"
          required
          value={clientId}
          onChange={(e) => setClientId(e.currentTarget.value)}
        >
          <option value="">Choose a client…</option>
          {(clients.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="mb-1 text-xs text-ink-soft">What it is</legend>
        <div className="flex flex-wrap gap-4">
          {(['dry_hire', 'produced_event'] as const).map((t) => (
            <label key={t} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="quote-type"
                checked={type === t}
                onChange={() => setType(t)}
              />
              {TYPE_LABELS[t]}
            </label>
          ))}
        </div>
        <p className="text-xs text-muted">
          {type === 'produced_event'
            ? 'Bump-in, show and bump-out, with gear in rooms. Can’t be changed to a dry hire later.'
            : 'Gear out and back. Can’t be changed to a produced event later.'}
        </p>
      </fieldset>
      <div className="flex flex-col gap-1.5">
        <VenuePicker
          id="quote-venue"
          label={type === 'produced_event' ? 'Venue' : 'Venue (optional)'}
          chosen={venue}
          noneLabel={type === 'produced_event' ? 'Choose later…' : 'No venue'}
          onChange={setVenueId}
          onNaming={setUnaddedVenue}
        />
        {type === 'produced_event' ? (
          <span className="text-xs text-muted">Needed before it is accepted, and for rooms.</span>
        ) : null}
      </div>
      {type === 'produced_event' ? (
        <>
          {(
            [
              ['bumpIn', 'Bump-in', 'The gear is committed from its start.'],
              ['event', 'Event', 'The show itself. Charged for its days by default.'],
              ['bumpOut', 'Bump-out', 'Committed until its end.'],
            ] as const
          ).map(([which, label, hint]) => (
            <div key={which} className="grid gap-4 sm:grid-cols-2">
              <Field
                label={`${label} from`}
                hint={hint}
                type="datetime-local"
                required
                value={events[which].start}
                onChange={(e) => setEvent(which, 'start', e.currentTarget.value)}
              />
              <Field
                label={`${label} to`}
                type="datetime-local"
                required
                value={events[which].end}
                onChange={(e) => setEvent(which, 'end', e.currentTarget.value)}
              />
            </div>
          ))}
        </>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Goes out"
            hint="Collection or delivery. The gear is committed from here."
            type="datetime-local"
            required
            value={out}
            onChange={(e) => setOut(e.currentTarget.value)}
          />
          <Field
            label="Comes back"
            hint="Return or collection. Committed until here."
            type="datetime-local"
            required
            value={back}
            onChange={(e) => setBack(e.currentTarget.value)}
          />
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="First day charged (optional)"
          hint={
            type === 'produced_event'
              ? 'Leave blank to charge from the event’s first day.'
              : 'Leave blank to charge from the day it goes out.'
          }
          type="date"
          value={chargedFrom}
          onChange={(e) => setChargedFrom(e.currentTarget.value)}
        />
        <Field
          label="Last day charged (optional)"
          hint={
            type === 'produced_event'
              ? 'Leave blank to charge to the event’s last day.'
              : 'Leave blank to charge to the day it comes back.'
          }
          type="date"
          value={chargedTo}
          onChange={(e) => setChargedTo(e.currentTarget.value)}
        />
      </div>
      {type === 'produced_event' ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Site contact (optional)"
              value={site.contactName}
              onChange={(e) => {
                const contactName = e.currentTarget.value;
                setSite((s) => ({ ...s, contactName }));
              }}
            />
            <Field
              label="Their phone (optional)"
              type="tel"
              value={site.contactPhone}
              onChange={(e) => {
                const contactPhone = e.currentTarget.value;
                setSite((s) => ({ ...s, contactPhone }));
              }}
            />
          </div>
          <Field
            label="Load and access notes (optional)"
            hint="Dock, parking, lifts, access times."
            value={site.loadNotes}
            onChange={(e) => {
              const loadNotes = e.currentTarget.value;
              setSite((s) => ({ ...s, loadNotes }));
            }}
          />
        </>
      ) : null}
      <Field
        label="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
      />
      <div className="flex gap-3">
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create quote'}
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel} disabled={create.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
